import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getProxiedImageUrl } from '@/utils/imageProxy';

interface SeoSettings {
  site_title: string;
  site_description: string;
  og_image: string;
  keywords: string;
  favicon_url: string;
}

const DynamicSEO = () => {
  const [seo, setSeo] = useState<SeoSettings | null>(null);

  useEffect(() => {
    const fetchSeo = async () => {
      const { data } = await supabase
        .from('page_content')
        .select('content')
        .eq('page_slug', 'seo')
        .maybeSingle();

      if (data?.content) {
        const c = data.content as Record<string, unknown>;
        setSeo({
          site_title: (c.site_title as string) || '',
          site_description: (c.site_description as string) || '',
          og_image: (c.og_image as string) || '',
          keywords: (c.keywords as string) || '',
          favicon_url: (c.favicon_url as string) || '',
        });
      }
    };
    fetchSeo();
  }, []);

  useEffect(() => {
    if (!seo) return;

    // Title
    if (seo.site_title) {
      document.title = seo.site_title;
      updateMeta('og:title', seo.site_title);
      updateMeta('twitter:title', seo.site_title);
    }

    // Description
    if (seo.site_description) {
      updateMeta('description', seo.site_description, 'name');
      updateMeta('og:description', seo.site_description);
    }

    // Keywords
    if (seo.keywords) {
      updateMeta('keywords', seo.keywords, 'name');
    }

    // OG Image
    if (seo.og_image) {
      const proxied = getProxiedImageUrl(seo.og_image) || seo.og_image;
      updateMeta('og:image', proxied);
      updateMeta('twitter:image', proxied);
    }

    // Favicon
    if (seo.favicon_url) {
      const proxied = getProxiedImageUrl(seo.favicon_url) || seo.favicon_url;
      updateFavicon(proxied);
    }
  }, [seo]);

  return null;
};

function updateMeta(key: string, value: string, attr: 'property' | 'name' = 'property') {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.querySelector(`meta[property="${key}"]`) as HTMLMetaElement | null;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function updateFavicon(url: string) {
  // Update link[rel="icon"]
  let icon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (icon) {
    icon.href = url;
  } else {
    icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = url;
    document.head.appendChild(icon);
  }

  // Update apple-touch-icon
  let apple = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
  if (apple) {
    apple.href = url;
  }
}

export default DynamicSEO;
